import React from 'react';
import { Link } from 'react-router-dom';
import { Hidden } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { DividerSection } from '../../../global/components/layout/divider-section';
import { TcsmStates } from '../../../root/tcsm-model';
import { ClergyBioSettings } from '../../arc/models/arc-epi-props';
import { ClergyBioChurchProfessional, DataState, ClergyBioRoutes } from '../model/clergy-bio-app-models';
import ClergyBioComponentLoader from '../components/clergy-bio-component-loader';
import { ContentSpacer } from '../../../global/components/layout/generic-content-spacer';
import { sortTableStyles } from '../../../global/constants/theme/styles/tables/generic-sort-table';
import { WysiwygWithReactLink } from '../../../global/components/wysiwyg-with-react-link';
import { buttonStyles } from '../../../global/constants/theme/styles/components/buttonstyles';
import { headerIconStyles } from '../../../global/constants/theme/styles/components/header-icon-styles';
import { ClericIcon } from '../../account-info/components/cleric-indicator';

interface Props {
	settings: ClergyBioSettings;
	basePath: string;
}

function ClergyBioProfessionalReview(props: Props) {

	const { settings, basePath } = props;
	const loadingState = useSelector<TcsmStates, DataState>(state => state?.clergyBioState?.loaded?.professional);
	const professional = useSelector<TcsmStates, ClergyBioChurchProfessional>(state => state?.clergyBioState?.professional);
	const tableClasses = sortTableStyles();
	const buttonClasses = buttonStyles();
	const headerClasses = headerIconStyles();

	function renderContent() {
		return (
			<>
				<ContentSpacer>
					<div className={headerClasses.iconHeader}>
						<ClericIcon />
						<h3>{settings?.publicationsTitle}</h3>
					</div>
					{
						professional?.publications?.details?.length > 0 ?
							<div className={tableClasses.sortTable}>
								<Hidden xsDown>
									<div className='sort-table-row'>
										<div className='sort-table-row-cell'><p><strong>{settings?.roleLabel}</strong></p></div>
										<div className='sort-table-row-cell'><p><strong>{settings?.titleOfWorkLabel}</strong></p></div>
										<div className='sort-table-row-cell'><p><strong>{settings?.nameOfPublicationLabel}</strong></p></div>
										<div className='sort-table-row-cell'><p><strong>{settings?.publisherLabel}</strong></p></div>
										<div className='sort-table-row-cell'><p><strong>{settings?.yearLabel}</strong></p></div>
									</div>
								</Hidden>
								<div className='sort-table-content'>
									{
										professional?.publications?.details?.map(
											(p) =>
												<div key={p?.id} className='sort-table-row'>
													<div className='sort-table-row-cell'>
														<Hidden smUp>
															<p><strong>{settings?.roleLabel}</strong></p>
														</Hidden>
														<p>{p?.role}</p>
													</div>
													<div className='sort-table-row-cell'>
														<Hidden smUp>
															<p><strong>{settings?.titleOfWorkLabel}</strong></p>
														</Hidden>
														<p>{p?.title}</p>
													</div>
													<div className='sort-table-row-cell'>
														<Hidden smUp>
															<p><strong>{settings?.nameOfPublicationLabel}</strong></p>
														</Hidden>
														<p>{p?.publication}</p>
													</div>
													<div className='sort-table-row-cell'>
														<Hidden smUp>
															<p><strong>{settings?.publisherLabel}</strong></p>
														</Hidden>
														<p>{p?.publisher}</p>
													</div>
													<div className='sort-table-row-cell'>
														<Hidden smUp>
															<p><strong>{settings?.yearLabel}</strong></p>
														</Hidden>
														<p>{p?.year}</p>
													</div>
												</div>,
										)
									}
								</div>
							</div> :
							<WysiwygWithReactLink content={settings?.noInformationText} />
					}
				</ContentSpacer>
				<ContentSpacer>
					<div className={headerClasses.iconHeader}>
						<ClericIcon />
						<h3>{settings?.honorsTitle}</h3>
					</div>
					{
						professional?.honors?.details?.length > 0 ?
							<div className={tableClasses.sortTable}>
								<Hidden xsDown>
									<div className='sort-table-row'>
										<div className='sort-table-row-cell'><p><strong>{settings?.honorLabel}</strong></p></div>
										<div className='sort-table-row-cell'><p><strong>{settings?.conferringOrgLabel}</strong></p></div>
										<div className='sort-table-row-cell'><p><strong>{settings?.yearLabel}</strong></p></div>
									</div>
								</Hidden>
								<div className='sort-table-content'>
									{
										professional?.honors?.details?.map(
											(h) =>
												<div key={h?.id} className='sort-table-row'>
													<div className='sort-table-row-cell'>
														<Hidden smUp>
															<p><strong>{settings?.honorLabel}</strong></p>
														</Hidden>
														<p>{h?.honor}</p>
													</div>
													<div className='sort-table-row-cell'>
														<Hidden smUp>
															<p><strong>{settings?.conferringOrgLabel}</strong></p>
														</Hidden>
														<p>{h?.organization}</p>
													</div>
													<div className='sort-table-row-cell'>
														<Hidden smUp>
															<p><strong>{settings?.yearLabel}</strong></p>
														</Hidden>
														<p>{h?.year}</p>
													</div>
												</div>,
										)
									}
								</div>
							</div> :
							<WysiwygWithReactLink content={settings?.noInformationText} />
					}

				</ContentSpacer>
				<ContentSpacer>
					<div className={headerClasses.iconHeader}>
						<ClericIcon />
						<h3>{settings?.societiesTitle}</h3>
					</div>
					{
						professional?.societies?.details?.length > 0 ?
							<div className={tableClasses.sortTable}>
								<Hidden xsDown>
									<div className='sort-table-row'>
										<div className='sort-table-row-cell'><p><strong>{settings?.yearBeginLabel}</strong></p></div>
										<div className='sort-table-row-cell'><p><strong>{settings?.yearEndLabel}</strong></p></div>
										<div className='sort-table-row-cell'><p><strong>{settings?.nameOfSocietyLabel}</strong></p></div>
									</div>
								</Hidden>
								<div className='sort-table-content'>
									{
										professional?.societies?.details?.map(
											(s) =>
												<div key={s?.id} className='sort-table-row'>
													<div className='sort-table-row-cell'>
														<Hidden smUp>
															<p><strong>{settings?.yearBeginLabel}</strong></p>
														</Hidden>
														<p>{s?.startYear}</p>
													</div>
													<div className='sort-table-row-cell'>
														<Hidden smUp>
															<p><strong>{settings?.yearEndLabel}</strong></p>
														</Hidden>
														<p>{s?.endYear}</p>
													</div>
													<div className='sort-table-row-cell'>
														<Hidden smUp>
															<p><strong>{settings?.nameOfSocietyLabel}</strong></p>
														</Hidden>
														<p>{s?.name}</p>
													</div>
												</div>,
										)
									}
								</div>
							</div> :
							<WysiwygWithReactLink content={settings?.noInformationText} />
					}
				</ContentSpacer>
			</>
		);
	}

	return (
		<DividerSection sectionText={settings?.professionalTitle} >
			<ClergyBioComponentLoader
				state={loadingState}
				content={renderContent()}
			/>
			<ContentSpacer>
				<Link className={buttonClasses.primaryButton} to={`${basePath}${ClergyBioRoutes.Professional}`}>{settings?.updateButtonText}</Link>
			</ContentSpacer>
		</DividerSection>
	);
}


export default ClergyBioProfessionalReview;